"use client";

import * as React from "react";
import { addDays, differenceInCalendarDays, format, startOfDay } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { DateRange } from "react-day-picker";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toCapitalizeString } from "@/lib/internalApis";

interface DatePickerWithRangeProps
  extends React.HTMLAttributes<HTMLDivElement> {
  placeholder?: string;
  date: DateRange | undefined;
  setDate: (date: DateRange | undefined) => void;
  onValueChange: (date: DateRange | undefined) => void;
  tripType?: "oneway" | "roundtrip";
  titleFirst?: string;
  titleSecond?: string;
  onDepartureDateSelected?: () => void;
  onReturnDateSelected?: () => void;
  shouldOpenCalendar?: boolean;
  shouldOpenReturnCalendar?: boolean;
  onOnewayToRoundtripTransition?: () => void;
  /** Minimum nights between from and to. Hotel search should pass 1 so same-day checkout is blocked. */
  minNights?: number;
}

export function DatePickerWithRange({
  className,
  date,
  setDate,
  onValueChange,
  placeholder = "Pick a date",
  tripType = "roundtrip",
  titleFirst = "Departure Date",
  titleSecond = "Return Date",
  onDepartureDateSelected,
  onReturnDateSelected,
  shouldOpenCalendar,
  shouldOpenReturnCalendar,
  onOnewayToRoundtripTransition,
  minNights = 0,
}: DatePickerWithRangeProps) {
  const [fromPopoverOpen, setFromPopoverOpen] = React.useState(false);
  const [toPopoverOpen, setToPopoverOpen] = React.useState(false);

  // Initialize default dates if not provided
  React.useEffect(() => {
    if (!date?.from && !date?.to) {
      const today = new Date();
      const tomorrow = addDays(today, 1);
      const defaultRange = {
        from: today,
        to: tripType === "roundtrip" ? tomorrow : today,
      };
      setDate(defaultRange);
    }
  }, [date, setDate, tripType]);

  // Auto-open calendar effects
  React.useEffect(() => {
    if (shouldOpenCalendar) {
      setFromPopoverOpen(true);
    }
  }, [shouldOpenCalendar]);

  React.useEffect(() => {
    if (shouldOpenReturnCalendar) {
      setToPopoverOpen(true);
    }
  }, [shouldOpenReturnCalendar]);

  const handleFromDateSelect = (newDate: Date | undefined) => {
    // Always close the calendar first, regardless of the date value
    setFromPopoverOpen(false);

    if (newDate) {
      if (tripType === "oneway") {
        // For oneway, always update the date
        const onewayRange = { from: newDate, to: newDate };
        setDate(onewayRange);

        // For oneway, we don't call onValueChange to avoid triggering roundtrip switch
        // The form values are updated via setDate callback in search component

        // Always trigger auto-flow callback when user selects a date (even if same)
        // This indicates user is done with calendar selection
        if (onDepartureDateSelected) {
          onDepartureDateSelected();
        }
      } else {
        // Keep checkout only if it still satisfies the minimum stay
        const keepExistingTo =
          !!date?.to &&
          differenceInCalendarDays(startOfDay(date.to), startOfDay(newDate)) >=
            minNights;
        const updatedRange = {
          from: newDate,
          to: keepExistingTo ? date.to : addDays(newDate, Math.max(minNights, 1)),
        };
        setDate(updatedRange);
        onValueChange(updatedRange);

        // Trigger auto-flow callback (including when same date is re-selected)
        if (onDepartureDateSelected) {
          onDepartureDateSelected();
        }
      }
    } else {
      // Even if newDate is undefined/null, still trigger the callback to continue auto-flow
      if (onDepartureDateSelected) {
        onDepartureDateSelected();
      }
    }
  };

  const handleToDateSelect = (newDate: Date | undefined) => {
    // Always close the calendar first, regardless of the date value
    setToPopoverOpen(false);

    if (newDate && date?.from) {
      const nights = differenceInCalendarDays(
        startOfDay(newDate),
        startOfDay(date.from),
      );
      const updatedRange = {
        from: date.from,
        to:
          nights >= minNights
            ? newDate
            : addDays(date.from, Math.max(minNights, 0)),
      };
      setDate(updatedRange);
      onValueChange(updatedRange);

      // Trigger auto-flow callback
      if (onReturnDateSelected) {
        onReturnDateSelected();
      }
    } else {
      // Even if newDate is undefined/null or no from date, still trigger the callback
      if (onReturnDateSelected) {
        onReturnDateSelected();
      }
    }
  };

  // Handle clicking on disabled return date button (oneway mode)
  const handleDisabledReturnClick = () => {
    if (tripType === "oneway" && date?.from) {
      // Call the transition callback if provided
      if (onOnewayToRoundtripTransition) {
        onOnewayToRoundtripTransition();
      } else {
        // Fallback to original behavior if no callback provided
        const roundtripRange = {
          from: date.from,
          to: addDays(date.from, 1),
        };
        setDate(roundtripRange);
        onValueChange(roundtripRange);
      }
    }
  };

  const today = new Date();

  return (
    <div className={cn("grid gap-2 text-black", className)}>
      <div className="flex gap-2">
        {/* Departure Date */}
        <Popover open={fromPopoverOpen} onOpenChange={setFromPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant={"outline"}
              className={cn(
                "w-[50%] !pl-1 !h-[60px] justify-start text-left font-normal !py-0 rounded-r-none border-r-0",
                !date?.from && "text-muted-foreground"
              )}
            >
              <CalendarIcon className={`mr-2 h-6 w-6 text-primary`} />
              {date?.from ? (
                <div className="flex flex-col">
                  <p className="text-xs md:text-sm font-medium text-primary">
                    {toCapitalizeString(titleFirst)}
                  </p>
                  <p className="text-xs xl:text-sm font-medium text-primary">
                    {format(date.from, "LLL dd, y")}
                  </p>
                </div>
              ) : (
                <span>{placeholder}</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 text-black" align="start">
            <Calendar
              mode="single"
              selected={date?.from}
              defaultMonth={date?.from || today}
              onSelect={handleFromDateSelect}
              disabled={{ before: today }}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        {/* Return Date */}
        <Popover
          open={tripType === "roundtrip" ? toPopoverOpen : false}
          onOpenChange={tripType === "roundtrip" ? setToPopoverOpen : undefined}
        >
          <PopoverTrigger asChild>
            <Button
              variant={"outline"}
              onClick={
                tripType === "oneway" ? handleDisabledReturnClick : undefined
              }
              className={cn(
                "w-[50%] !pl-3 !h-[60px] justify-start text-left font-normal !py-0 rounded-l-none",
                tripType === "oneway" &&
                  "cursor-pointer bg-gray-50 text-gray-400 hover:bg-gray-100",
                tripType === "roundtrip" && !date?.to && "text-muted-foreground"
              )}
            >
              {date?.to && tripType === "roundtrip" ? (
                <div className="flex flex-col">
                  <p className="text-xs md:text-sm font-medium text-primary">
                    {toCapitalizeString(titleSecond)}
                  </p>
                  <p className="text-xs xl:text-sm font-medium text-secondary">
                    {format(date.to, "LLL dd, y")}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col">
                  <p
                    className={cn(
                      "text-xs md:text-sm font-medium",
                      tripType === "oneway" ? "text-gray-400" : "text-gray-400"
                    )}
                  >
                    {toCapitalizeString(titleSecond)}
                  </p>
                  <p
                    className={cn(
                      "text-xs xl:text-sm font-medium",
                      tripType === "oneway" ? "text-gray-400" : "text-gray-400"
                    )}
                  >
                    {format(Date.now() as any, "LLL dd, y")}
                    </p>
                </div>
              )}
            </Button>
          </PopoverTrigger>
          {tripType === "roundtrip" && (
            <PopoverContent className="w-auto p-0 text-black" align="start">
              <Calendar
                mode="single"
                selected={date?.to}
                defaultMonth={date?.to || date?.from || today}
                onSelect={handleToDateSelect}
                disabled={{
                  before: date?.from
                    ? addDays(startOfDay(date.from), minNights)
                    : today,
                }}
                initialFocus
              />
            </PopoverContent>
          )}
        </Popover>
      </div>
    </div>
  );
}
